// Editable chips on a card's header (spec/dashboard.md section 7.2; research/01 P16): a muted label and the value at
// weight 500, a click opens the field's widget in a popover; booleans toggle in place; a chip the draft changed is tinted
// and says "changed" to assistive technology.
import { useState } from 'react';
import type { Document, Json, Parameter } from '../../api/types';
import { useGame } from '../../shell/GameContext';
import { Popover } from '../../ui/Popover';
import { Switch } from '../../ui/inputs';
import { useTimeZone } from '../../ui/time';
import { useContent } from '../ContentContext';
import { formatValue, zeroWord } from '../format';
import { parameterHelp } from '../help';
import { FieldWidget } from '../widgets/FieldWidget';

export interface ChipProps {
  doc: Document;
  param: Parameter;
  onCommit: (value: Json) => void;
  disabled?: boolean;
  label?: string;
  /** the word `0` reads as (no cooldown, unlimited, infinite, ends with the event) */
  zeroMeans?: string;
  /** the chip is tinted when the draft changed this field */
  changed?: boolean;
}

export function Chip({ doc, param, onCommit, disabled, label, zeroMeans, changed }: ChipProps) {
  const catalog = useContent();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const value = ((doc.data ?? {}) as Record<string, Json>)[param.name];
  const title = label ?? param.displayName ?? param.name;
  if (param.type === 'bool') {
    return (
      <span className={`card-chip ${changed ? 'changed' : ''}`} title={changed ? 'Changed in your draft' : parameterHelp(param, doc, catalog.templates)}>
        <Switch label={title} checked={Boolean(value)} disabled={disabled} onChange={(v) => onCommit(v)} />
      </span>
    );
  }
  const text = (param.type === 'duration' || param.type === 'int') && Number(value ?? 0) === 0 && zeroMeans ? zeroMeans : formatValue(param, value, { ...catalog.format, zone, offsets });
  return (
    <>
      <button type="button" className={`card-chip ${changed ? 'changed' : ''}`} onClick={(e) => !disabled && setAnchor(e.currentTarget)} disabled={disabled} title={((help) => (changed ? `Changed in your draft${help ? ` · ${help}` : ''}` : help))(parameterHelp(param, doc, catalog.templates))} aria-haspopup="dialog">
        <span className="card-chip-label">{title}</span>
        <span className="card-chip-value">{text}</span>
        {changed && <span className="visually-hidden">, changed</span>}
      </button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label={title}>
        <div className="chip-popover">
          <FieldWidget
            param={{ ...param, displayName: title }}
            value={value}
            autoFocus
            owner={doc.id}
            onCommit={(v) => {
              onCommit(v);
              if (param.type === 'enum' || param.type === 'document' || param.type === 'asset' || param.type === 'product') setAnchor(null);
            }}
            onCancel={() => setAnchor(null)}
          />
          {param.type === 'duration' && <p className="muted small">{zeroMeans ? `0 = ${zeroMeans}` : zeroWord(param) ? `0 = ${zeroWord(param)}` : ''}</p>}
        </div>
      </Popover>
    </>
  );
}

/** The chips of a document's parameters by name, with their zero words. */
export function paramOf(params: Parameter[], name: string): Parameter | undefined {
  return params.find((p) => p.name === name);
}
